<template>
  <div class="mx-auto">
    <modal
      :tokenType="modalType"
      coverBackground
      :show="showModal"
      :id="getReadableId(selected)"
      @cancel="cancelModal"
      @agree="agreeAction"
    ></modal>
    <div v-if="encounters.length === 0" class="px-4 text-center">
      <mrx-translation token="app.encounter.none.title"></mrx-translation>
    </div>
    <table v-else class="mx-auto px-4">
      <tr>
        <th>
          <mrx-translation token="app.encounter.id.title"></mrx-translation>
        </th>
        <th>
          <mrx-translation token="app.encounter.actions.title"></mrx-translation>
        </th>
      </tr>
      <tr v-for="encounter in encounters" :key="encounter.session.uuid">
        <td>
          <div>
            <strong>{{ getReadableId(encounter) }}</strong>
          </div>
          <div v-for="row in getEncounterInformation(encounter)" :key="row.label" class="encounter-info">
            <strong>{{ row.label }}:</strong> {{ row.value }}
          </div>
        </td>
        <td>
          <v-btn
            color="var(--primary-blue)"
            style="font-size: 14px; font-weight: 600; color: white"
            depressed
            @click="openModal('resume', encounter)"
          >
            <mrx-translation token="app.dialog.button.resume"></mrx-translation>
          </v-btn>
          <v-btn
            class="ms-2"
            color="var(--danger)"
            style="color: white"
            depressed
            @click="openModal('delete', encounter)"
          >
            <v-icon>{{ icons.mdiTrashCanOutline }}</v-icon>
          </v-btn>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
  import ScrollArrow from '../layouts/ScrollArrow.vue'
  import Modal from '@/components/layouts/Modal'
  import scrollArrow from '@/mixins/scrollArrow'
  import icons from '@/mixins/icons'
  import ageRanges from '@/mixins/ageRanges'
  import { mapActions, mapState } from 'vuex'

  export default {
    name: "Encounters",
    mixins: [ageRanges, icons, scrollArrow],
    components: { Modal, ScrollArrow },
    data() {
      return {
        modalType: 'resume',
        showModal: false,
        selected: null,
      }
    },
    computed: {
      ...mapState('v4/encounters', {
        encounters: 'saved',
      }),
    },
    methods: {
      clearSelection() {
        this.modalType = 'resume'
        this.showModal = false
        this.selected = null
      },
      openModal(type, encounter) {
        this.modalType = type
        this.selected = encounter
        this.showModal = true
      },
      cancelModal() {
        this.clearSelection()
      },
      async agreeAction() {
        if (this.modalType === 'resume') {
          await this.resumeEncounter()
        } else {
          this.deleteEncounter()
        }
      },
      deleteEncounter() {
        this.delete(this.selected.session.uuid)
        this.clearSelection()
      },
      async resumeEncounter() {
        const { trackId, panel } = await this.load(this.selected.session.uuid)
        this.$track(this.$analyticsEvents.ENCOUNTER_RESUMED, {
          component: 'Encounters',
          encounter_id: this.getReadableId(this.selected),
          resumed_panel: panel
        })
        this.clearSelection()
        this.$router.push({ path: `/track/${trackId}/${panel}` })
      },
      getReadableId(encounter) {
        // Determine which panel name to use for the readable ID
        const pauseResumePanelName = this.$store.state.settings.engagements_features?.encounterPauseResume || null
        let idString = this.$t('app.encounter.no_id.title')
        try {
          if (pauseResumePanelName) {
            const panel = encounter.track.current.panels.find(p => p.name === pauseResumePanelName)
            if (panel && panel.responses && panel.responses[0] && panel.responses[0].value) {
              idString = panel.responses[0].value.toString()
            }
          } else {
        // Fallback: check visit_id, then patient_id
        const visitIdPanel = encounter.track.current.panels.find(p => p.name === 'visit_id')
        if (visitIdPanel && visitIdPanel.responses && visitIdPanel.responses[0] && visitIdPanel.responses[0].value) {
          idString = visitIdPanel.responses[0].value.toString()
        } else {
          const patientIdPanel = encounter.track.current.panels.find(p => p.name === 'patient_id')
          if (patientIdPanel && patientIdPanel.responses && patientIdPanel.responses[0] && patientIdPanel.responses[0].value) {
            idString = patientIdPanel.responses[0].value.toString()
          }
        }
          }
        } catch (e) { }
        return idString
      },

      getPatientAge(encounter) {
        try {
          const agePanel = encounter.track.current.panels.find(p => p.name === 'age')
          const ageValue = agePanel && agePanel.responses && agePanel.responses[0] ? agePanel.responses[0].value : null
          return `${this.ageDisplayString(ageValue)}`
        } catch (e) {
          console.error('Error getting patient age:', e)
          return ''
        }
      },
      getPatientGender(encounter) {
        try {
          const genderPanel = encounter.track.current.panels.find(p => p.name === 'gender')
          
          if (genderPanel && genderPanel.computation && genderPanel.computation == 'assignFemaleGender') {
            return 'female'
          }

          if (genderPanel && genderPanel.responses && genderPanel.responses.length > 0) {
            const selectedResponse = genderPanel.responses.find(r => r.selected)
            return selectedResponse && selectedResponse.value ? selectedResponse.value : ''
          }
          return ''
        } catch (e) {
          return ''
        }
      },
      getEncounterInformation(encounter) {
        const info = [];
        const age = this.getPatientAge(encounter);
        if (age !== '') {
          info.push({ label: this.$t('questions.age.title'), value: age });
        }
        const genderValue = this.getPatientGender(encounter);
        if (genderValue !== '') {
          info.push({ label: this.$t('questions.gender.title'), value: this.$t(`responses.gender.${genderValue}.title`) });
        }

        return info;
      },
      ...mapActions({
        load: 'v4/encounters/load',
        delete: 'v4/encounters/delete',
        next: 'v4/navigate/next',
      })
    },
  }
</script>

<style scoped>
  th, td {
    padding: 12px;
  }
  th {
    background-color: var(--primary-blue);
    font-size: 1.2em;
  }
  tr:nth-child(2n+3){
    background-color: var(--primary-blue-30);
  }
  table, th, td {
    border: 1px solid var(--primary-blue);
    border-collapse: collapse;
  }
</style>